import { CATALOGUE, DIFFICULTIES, MAPS, WAVE_SCRIPTS, structureLabel } from '../game/content/data';
import type { AbilityId, CatalogueId, DifficultyId, MapDefinition, MapId } from '../game/content/types';
import { Simulation } from '../game/simulation/Simulation';
import type { RunMode, SimulationSnapshot } from '../game/simulation/types';
import { completedCyclesForClearedWaves, infiniteHealthMultiplier, resolveWave } from '../game/simulation/waveResolver';
import { AudioDirector } from '../phaser/audio/AudioDirector';

export type Screen = 'title' | 'maps' | 'difficulty' | 'board' | 'help' | 'scores';
export type PanelTab = 'basic' | 'advanced' | 'buildings';
export type BoardMode = { kind: 'idle' } | { kind: 'build'; structureId: CatalogueId } | { kind: 'sell' } | { kind: 'ability'; ability: AbilityId };

interface Hover { x: number; y: number; }
export interface StoredScore {
  score: number;
  mapId: MapId;
  difficultyId: DifficultyId;
  mode: RunMode;
  outcome: 'victory' | 'defeat' | 'quit';
  tick: number;
  highestWave: number;
  completedCycles: number;
  survivalTicks: number;
}

export interface ControllerRenderState {
  screen: Screen;
  selectedMap: MapId;
  selectedDifficulty: DifficultyId;
  selectedRunMode: RunMode;
  tab: PanelTab;
  mode: BoardMode;
  hover: Hover | null;
  sim: Simulation | null;
  reducedMotion: boolean;
}

export const formatWavePreview = (mapId: MapId, difficultyId: DifficultyId, index: number, mode: RunMode = 'fixed'): string => {
  const resolved = resolveWave(WAVE_SCRIPTS[mapId][difficultyId], difficultyId, mode, index + 1);
  if (!resolved) return 'RUN COMPLETE';
  const groups = resolved.definition.groups.map((group) => `${group.count} ${group.size[0].toUpperCase()}${group.size.slice(1)} Lvl ${group.level} ${group.domain.toUpperCase()}${group.effect ? ` / ${group.effect.toUpperCase()}` : ''}`);
  const context = mode === 'infinite' ? ` \u00b7 CYCLE ${resolved.cycle} / BASE ${resolved.baseWave} / HP ${resolved.hpPercent}%` : '';
  return `#${resolved.absoluteWave}${context} \u00b7 ${groups.join(' + ')}`;
};

export const formatCountdown = (ticks: number): string => {
  const seconds = Math.max(0, Math.ceil(ticks / 24));
  return `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;
};

export const formatSurvivalTime = (ticks: number): string => formatCountdown(ticks);

const escapeHtml = (value: string): string => value.replace(/[&<>"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[character] ?? character);
const SCORE_STORAGE_KEY = 'cd1.completed-scores.v1';
const isMapId = (value: unknown): value is MapId => value === 'map-1' || value === 'map-2' || value === 'map-3';
const isDifficultyId = (value: unknown): value is DifficultyId => value === 'easy' || value === 'normal' || value === 'hard';
const isRunMode = (value: unknown): value is RunMode => value === 'fixed' || value === 'infinite';
const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const isNonNegativeInteger = (value: unknown): value is number => isFiniteNumber(value) && Number.isInteger(value) && value >= 0;

/** Reads both v1 legacy Fixed entries and the mode-aware score shape. */
export const normalizeStoredScore = (value: unknown): StoredScore | null => {
  if (typeof value !== 'object' || value === null) return null;
  const entry = value as Record<string, unknown>;
  if (!isFiniteNumber(entry.score) || !isFiniteNumber(entry.tick) || !isMapId(entry.mapId) || !isDifficultyId(entry.difficultyId)) return null;
  if (entry.mode !== undefined && !isRunMode(entry.mode)) return null;
  const mode: RunMode = entry.mode ?? 'fixed';
  if (mode === 'fixed' && entry.outcome !== 'victory' && entry.outcome !== 'defeat') return null;
  if (mode === 'infinite' && entry.outcome !== 'defeat' && entry.outcome !== 'quit') return null;
  const outcome = entry.outcome as StoredScore['outcome'];
  if (mode === 'infinite' && (!isNonNegativeInteger(entry.highestWave) || !isNonNegativeInteger(entry.completedCycles) || !isNonNegativeInteger(entry.survivalTicks))) return null;
  return {
    score: entry.score,
    mapId: entry.mapId,
    difficultyId: entry.difficultyId,
    mode,
    outcome,
    tick: entry.tick,
    highestWave: isNonNegativeInteger(entry.highestWave) ? entry.highestWave : 0,
    completedCycles: isNonNegativeInteger(entry.completedCycles) ? entry.completedCycles : 0,
    survivalTicks: isNonNegativeInteger(entry.survivalTicks) ? entry.survivalTicks : Math.max(0, Math.floor(entry.tick)),
  };
};

export const scoreCategoryKey = (entry: Pick<StoredScore, 'mode' | 'mapId' | 'difficultyId'>): string =>
  `${entry.mode}:${entry.mapId}:${entry.difficultyId}`;

export const rankScoresByCategory = (scores: readonly StoredScore[]): readonly StoredScore[] => {
  const categories = new Map<string, StoredScore[]>();
  scores.forEach((entry) => {
    const key = scoreCategoryKey(entry);
    categories.set(key, [...(categories.get(key) ?? []), entry]);
  });
  return [...categories.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .flatMap(([, entries]) => entries
      .sort((left, right) => right.score - left.score || right.highestWave - left.highestWave || right.survivalTicks - left.survivalTicks)
      .slice(0, 10));
};

export const storedScoreForSnapshot = (snapshot: SimulationSnapshot, outcome: StoredScore['outcome']): StoredScore => {
  const cycleLength = WAVE_SCRIPTS[snapshot.mapId][snapshot.difficultyId].length;
  return {
    score: snapshot.score,
    mapId: snapshot.mapId,
    difficultyId: snapshot.difficultyId,
    mode: snapshot.runMode,
    outcome,
    tick: snapshot.tick,
    highestWave: snapshot.nextWaveIndex,
    completedCycles: completedCyclesForClearedWaves(snapshot.completedWaves, cycleLength),
    survivalTicks: snapshot.tick,
  };
};

const abilityLabel = (ability: AbilityId): string => ability === 'stop-time' ? 'Stop Time' : ability[0].toUpperCase() + ability.slice(1);
const abilityRequirement = (ability: AbilityId): string => ({
  missile: 'Build a Nuclear Silo to unlock Missile.',
  wall: 'Build a Wall Factory to unlock Wall.',
  'stop-time': 'Build a Laboratory to unlock Stop Time.',
}[ability]);

const structureIcon = (id: CatalogueId): string => {
  if (id.startsWith('cd1.tower.')) return `assets/units/tower-${id.replace('cd1.tower.', '')}.png`;
  const stem = id.replace('cd1.', '').replace('.', '-');
  return `assets/units/${stem}.png`;
};

const scoreListMarkup = (scores: readonly StoredScore[]): string => {
  const categories = new Map<string, StoredScore[]>();
  scores.forEach((entry) => {
    const key = scoreCategoryKey(entry);
    categories.set(key, [...(categories.get(key) ?? []), entry]);
  });
  return [...categories.values()].map((entries) => {
    const category = entries[0];
    const heading = `${category.mode === 'fixed' ? 'FIXED' : 'INFINITE'} · MAP ${category.mapId.slice(-1)} · ${category.difficultyId.toUpperCase()}`;
    return `<section class="cd1-score-category"><h3>${escapeHtml(heading)}</h3><ol class="cd1-score-list">${entries.map((entry) => {
      const detail = entry.mode === 'infinite'
        ? `WAVE ${entry.highestWave} · ${entry.completedCycles} CYCLES · ${formatSurvivalTime(entry.survivalTicks)} · ${entry.outcome.toUpperCase()}`
        : entry.outcome.toUpperCase();
      return `<li><b>${escapeHtml(String(entry.score))}</b><span>${escapeHtml(detail)}</span></li>`;
    }).join('')}</ol></section>`;
  }).join('');
};

/** The menu preview is rendered from the same route/build topology used by the simulation. */
const mapThumbnail = (map: MapDefinition): string => map.surfaceRows.flatMap((row, y) => [...row].map((_surface, x) => {
  const isRoute = map.roadCells.includes(`${x},${y}`);
  return `<i class="cd1-map-cell ${isRoute ? 'route' : 'terrain'}"></i>`;
})).join('');

/** DOM operator shell with a stable interaction tree; only small live text and state classes update during ticks. */
export class GameController {
  private root: HTMLElement | null = null;
  private screen: Screen = 'title';
  private selectedMap: MapId = 'map-1';
  private selectedDifficulty: DifficultyId = 'easy';
  private selectedRunMode: RunMode = 'fixed';
  private tab: PanelTab = 'basic';
  private mode: BoardMode = { kind: 'idle' };
  private hover: Hover | null = null;
  private sim: Simulation | null = null;
  private accumulator = 0;
  private hudRefreshAccumulator = 0;
  private modal: 'pause' | 'quit' | 'result' | null = null;
  private quitWasPaused = false;
  private uiNotice = '';
  private recordedResultKey: string | null = null;
  private knownUnlocks = new Set<CatalogueId>();
  private unlockBannerQueue: CatalogueId[] = [];
  private activeUnlockBanner: CatalogueId | null = null;
  private unlockBannerTimer: number | null = null;
  private readonly reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  private readonly audio = new AudioDirector();

  mount(root: HTMLElement): void {
    this.root = root;
    root.innerHTML = `
      <section class="app-shell">
        <div class="game-stage" data-testid="game-stage">
          <div class="world-wrap"><div id="phaser-host" aria-label="Canyon battlefield"></div></div>
          <aside id="operator-panel" aria-label="Operator control panel"></aside>
          <div id="screen-overlay"></div>
          <div id="modal-overlay"></div>
          <div id="unlock-banner-region" aria-live="polite" aria-atomic="true"></div>
        </div>
        <p id="screen-reader-status" class="sr-only" aria-live="polite"></p>
      </section>`;
    root.addEventListener('click', this.onClick);
    this.audio.arm(root);
    window.addEventListener('keydown', this.onKeyDown);
    this.render();
  }

  destroy(): void {
    this.resetUnlockBanners(null);
    this.root?.removeEventListener('click', this.onClick);
    this.audio.destroy(this.root);
    window.removeEventListener('keydown', this.onKeyDown);
  }

  getRenderState(): ControllerRenderState {
    return { screen: this.screen, selectedMap: this.selectedMap, selectedDifficulty: this.selectedDifficulty, selectedRunMode: this.selectedRunMode, tab: this.tab, mode: this.mode, hover: this.hover, sim: this.sim, reducedMotion: this.reducedMotion };
  }

  getSimulation(): Simulation | null { return this.sim; }

  advance(deltaMilliseconds: number): void {
    if (!this.sim || this.screen !== 'board') return;
    this.accumulator += Math.min(deltaMilliseconds, 100) / 1000;
    const tickLength = this.sim.getTickSeconds();
    let steps = 0;
    while (this.accumulator >= tickLength && steps < 6) {
      this.sim.step();
      this.accumulator -= tickLength;
      steps += 1;
    }
    if (steps > 0) {
      const snapshot = this.sim.getSnapshot();
      this.audio.sync(snapshot);
      this.syncUnlockBanners(snapshot);
    }
    this.hudRefreshAccumulator += deltaMilliseconds;
    if (steps > 0 && this.hudRefreshAccumulator >= 110) {
      this.hudRefreshAccumulator = 0;
      const panel = this.root?.querySelector<HTMLElement>('#operator-panel');
      if (panel?.dataset.structureKey !== this.panelStructureKey()) this.renderBoard();
      else this.patchBoardHud();
      this.updateResultModal();
    }
  }

  setHover(x: number, y: number): void { this.hover = { x, y }; }
  clearHover(): void { this.hover = null; }

  boardPointer(x: number, y: number): void {
    if (!this.sim || this.screen !== 'board' || this.modal) return;
    const actionMode = this.mode;
    let successful = false;
    if (this.mode.kind === 'build') successful = this.sim.placeStructure(this.mode.structureId, x, y);
    else if (this.mode.kind === 'sell') successful = this.sim.sellAt(x, y);
    else if (this.mode.kind === 'ability') {
      successful = this.sim.useAbility(this.mode.ability, x, y);
      if (successful) this.mode = { kind: 'idle' };
    }
    const snapshot = this.sim.getSnapshot();
    this.uiNotice = snapshot.lastNotice;
    if (successful) {
      if (actionMode.kind === 'build') this.audio.play('construction');
      if (actionMode.kind === 'sell') this.audio.play('sell');
      if (actionMode.kind === 'ability' && actionMode.ability === 'wall') this.audio.play('construction');
      this.audio.sync(snapshot);
    } else if (actionMode.kind !== 'idle') {
      this.audio.play('error');
    }
    this.syncUnlockBanners(snapshot);
    this.renderBoard();
  }

  chooseMap(mapId: MapId): void { this.selectedMap = mapId; this.screen = 'difficulty'; this.render(); }

  chooseRunMode(runMode: RunMode): void {
    this.selectedRunMode = runMode;
    this.audio.play('switch');
    this.renderScreen();
  }

  chooseDifficulty(difficultyId: DifficultyId, runMode: RunMode = this.selectedRunMode): void {
    this.selectedDifficulty = difficultyId;
    this.selectedRunMode = runMode;
    this.sim = new Simulation(this.selectedMap, difficultyId, runMode);
    this.screen = 'board';
    this.tab = 'basic';
    this.mode = { kind: 'idle' };
    this.modal = null;
    this.uiNotice = '';
    this.recordedResultKey = null;
    this.accumulator = 0;
    this.hudRefreshAccumulator = 0;
    const snapshot = this.sim.getSnapshot();
    this.resetUnlockBanners(snapshot);
    this.audio.beginRun(snapshot);
    this.audio.setPaused(false);
    this.render();
  }

  startWave(): void {
    if (!this.sim) return;
    if (!this.sim.getSnapshot().nextWave) {
      this.showNotice(this.sim.getState().phase === 'victory' ? 'Run complete: all attacks survived.' : 'WAIT: clear the current hostiles to complete the run.');
      return;
    }
    if (this.sim.startNextWave()) {
      this.uiNotice = '';
      this.audio.sync(this.sim.getSnapshot());
    } else {
      this.uiNotice = this.sim.getSnapshot().lastNotice;
      this.audio.play('error');
    }
    this.patchBoardHud();
  }

  selectBuild(structureId: CatalogueId): void {
    if (!this.sim) return;
    const entry = CATALOGUE.find((candidate) => candidate.id === structureId);
    if (!this.sim.isUnlocked(structureId)) { this.audio.play('error'); this.showNotice(entry?.requirement ?? 'This construction is locked.'); return; }
    this.uiNotice = '';
    this.mode = { kind: 'build', structureId };
    this.audio.play('switch');
    this.patchBoardHud();
  }

  selectAbility(ability: AbilityId): void {
    if (!this.sim) return;
    const sourceCount = this.sim.getAbilitySourceCount(ability);
    if (sourceCount === 0) { this.audio.play('error'); this.showNotice(abilityRequirement(ability)); return; }
    if (!this.sim.abilityReady(ability)) { this.audio.play('error'); this.showNotice(`${abilityLabel(ability)} is recharging.`); return; }
    this.uiNotice = '';
    this.mode = { kind: 'ability', ability };
    this.audio.play('switch');
    this.patchBoardHud();
  }

  selectSell(): void { this.uiNotice = ''; this.mode = this.mode.kind === 'sell' ? { kind: 'idle' } : { kind: 'sell' }; this.audio.play('switch'); this.patchBoardHud(); }
  setTab(tab: PanelTab): void {
    this.tab = tab;
    this.mode = { kind: 'idle' };
    this.uiNotice = '';
    this.audio.play('switch');
    this.renderBoard();
    this.root?.querySelector<HTMLButtonElement>(`[data-tab="${tab}"]`)?.focus();
  }

  togglePause(): void {
    if (!this.sim || this.modal === 'quit' || this.modal === 'result') return;
    if (this.sim.getState().paused) {
      if (!this.sim.setPaused(false)) return;
      this.modal = null;
    } else {
      if (!this.sim.setPaused(true)) return;
      this.modal = 'pause';
    }
    this.audio.play('switch');
    this.audio.setPaused(this.sim.getState().paused);
    this.render();
  }

  restart(): void { this.chooseDifficulty(this.selectedDifficulty, this.selectedRunMode); }

  returnToTitle(): void {
    this.resetUnlockBanners(null);
    this.sim = null;
    this.screen = 'title';
    this.mode = { kind: 'idle' };
    this.modal = null;
    this.uiNotice = '';
    this.audio.setPaused(false);
    this.render();
  }

  snapshot(): SimulationSnapshot | null { return this.sim?.getSnapshot() ?? null; }

  /** Test-only deterministic advancement that still updates controller-owned HUD/modal state. */
  debugTick(count: number): void {
    this.sim?.step(count);
    if (this.sim) {
      const snapshot = this.sim.getSnapshot();
      this.audio.sync(snapshot);
      this.syncUnlockBanners(snapshot);
    }
    this.renderBoard();
    this.updateResultModal();
  }

  /** Test-only setup for visual regression captures; ordinary UI actions cannot call this. */
  debugPrimeVisualQa(): void {
    this.sim?.debugPrimeVisualQa();
    if (this.sim) {
      const snapshot = this.sim.getSnapshot();
      this.audio.sync(snapshot);
      this.resetUnlockBanners(snapshot);
    }
    this.renderBoard();
  }

  /** Test-only single transition used to verify queued unlock presentation. */
  debugUnlockForQa(definitionId: CatalogueId): boolean {
    if (!this.sim?.debugUnlockForQa(definitionId)) return false;
    const snapshot = this.sim.getSnapshot();
    this.audio.sync(snapshot);
    this.syncUnlockBanners(snapshot);
    this.renderBoard();
    return true;
  }

  private resetUnlockBanners(snapshot: SimulationSnapshot | null): void {
    if (this.unlockBannerTimer !== null) window.clearTimeout(this.unlockBannerTimer);
    this.unlockBannerTimer = null;
    this.unlockBannerQueue = [];
    this.activeUnlockBanner = null;
    this.knownUnlocks = new Set(
      snapshot ? CATALOGUE.filter((entry) => snapshot.unlocked[entry.id]).map((entry) => entry.id) : [],
    );
    this.root?.querySelector('#unlock-banner-region')?.replaceChildren();
  }

  private syncUnlockBanners(snapshot: SimulationSnapshot): void {
    const currentUnlocks = new Set(
      CATALOGUE.filter((entry) => snapshot.unlocked[entry.id]).map((entry) => entry.id),
    );
    CATALOGUE.forEach((entry) => {
      if (currentUnlocks.has(entry.id) && !this.knownUnlocks.has(entry.id)) {
        this.unlockBannerQueue.push(entry.id);
      }
    });
    this.knownUnlocks = currentUnlocks;
    this.showNextUnlockBanner();
  }

  private showNextUnlockBanner(): void {
    if (this.activeUnlockBanner || this.screen !== 'board') return;
    const definitionId = this.unlockBannerQueue.shift();
    if (!definitionId) return;
    const entry = CATALOGUE.find((candidate) => candidate.id === definitionId);
    const region = this.root?.querySelector<HTMLElement>('#unlock-banner-region');
    if (!entry || !region) return;

    const banner = document.createElement('section');
    banner.className = 'cd1-unlock-banner';
    banner.dataset.testid = 'unlock-banner';
    banner.setAttribute('role', 'status');
    const type = document.createElement('span');
    type.textContent = entry.tab === 'buildings' ? 'BUILDING UNLOCKED' : 'TURRET UNLOCKED';
    const label = document.createElement('strong');
    label.textContent = entry.label;
    banner.append(type, label);
    region.replaceChildren(banner);
    this.activeUnlockBanner = definitionId;
    this.unlockBannerTimer = window.setTimeout(() => {
      region.replaceChildren();
      this.activeUnlockBanner = null;
      this.unlockBannerTimer = null;
      this.showNextUnlockBanner();
    }, 2_800);
  }

  private openQuit(): void {
    if (!this.sim) return;
    this.quitWasPaused = this.sim.getState().paused;
    if (!this.quitWasPaused) this.sim.setPaused(true);
    this.audio.play('switch');
    this.audio.setPaused(true);
    this.modal = 'quit';
    this.patchBoardHud();
    this.renderModal();
  }

  private cancelQuit(): void {
    if (!this.sim) { this.modal = null; this.renderModal(); return; }
    if (!this.quitWasPaused) this.sim.setPaused(false);
    this.audio.play('switch');
    this.audio.setPaused(this.sim.getState().paused);
    this.modal = this.quitWasPaused ? 'pause' : null;
    this.quitWasPaused = false;
    this.patchBoardHud();
    this.renderModal();
  }

  private showNotice(message: string): void { this.uiNotice = message; this.patchBoardHud(); this.updateLiveStatus(); }

  private onClick = (event: MouseEvent): void => {
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-action]');
    if (!target) return;
    this.audio.unlock();
    const modalOverlay = this.root?.querySelector('#modal-overlay');
    if (this.modal && !modalOverlay?.contains(target)) return;
    const action = target.dataset.action;
    if (action === 'toggle-music') { this.audio.toggleMusic(); this.patchBoardHud(); return; }
    if (action === 'toggle-sfx') { this.audio.toggleSfx(); this.patchBoardHud(); return; }
    if (action === 'open-maps') { this.audio.play('switch'); this.screen = 'maps'; this.render(); return; }
    if (action === 'open-help') { this.audio.play('switch'); this.screen = 'help'; this.render(); return; }
    if (action === 'open-scores') { this.audio.play('switch'); this.screen = 'scores'; this.render(); return; }
    if (action === 'back-title') { this.audio.play('switch'); this.screen = 'title'; this.render(); return; }
    if (action === 'select-map') { this.audio.play('switch'); this.chooseMap(target.dataset.map as MapId); return; }
    if (action === 'select-run-mode') { this.chooseRunMode(target.dataset.runMode as RunMode); return; }
    if (action === 'select-difficulty') { this.audio.play('switch'); this.chooseDifficulty(target.dataset.difficulty as DifficultyId); return; }
    if (action === 'back-maps') { this.audio.play('switch'); this.screen = 'maps'; this.render(); return; }
    if (action === 'tab') { this.setTab(target.dataset.tab as PanelTab); return; }
    if (action === 'build') { this.selectBuild(target.dataset.structure as CatalogueId); return; }
    if (action === 'sell') { this.selectSell(); return; }
    if (action === 'ability') { this.selectAbility(target.dataset.ability as AbilityId); return; }
    if (action === 'play') { this.startWave(); return; }
    if (action === 'pause' || action === 'resume') { this.togglePause(); return; }
    if (action === 'open-quit') { this.openQuit(); return; }
    if (action === 'cancel-quit') { this.cancelQuit(); return; }
    if (action === 'quit-title') {
      this.audio.play('switch');
      if (this.sim?.getState().runMode === 'infinite') this.recordScore('quit');
      this.returnToTitle();
      return;
    }
    if (action === 'restart') { this.audio.play('switch'); this.restart(); return; }
    if (action === 'result-title') { this.audio.play('switch'); this.returnToTitle(); }
  };

  private onKeyDown = (event: KeyboardEvent): void => {
    if (this.modal) return;
    if (event.code === 'Space' && this.screen === 'board' && !this.hasInteractiveFocus()) {
      event.preventDefault();
      this.startWave();
      return;
    }
    if ((event.code === 'KeyP' || event.code === 'Escape') && this.screen === 'board') {
      event.preventDefault();
      this.togglePause();
    }
  };

  private hasInteractiveFocus(): boolean {
    const active = document.activeElement;
    return active instanceof HTMLElement && active.matches('button, input, select, textarea, a[href], [contenteditable="true"], [role="button"]');
  }

  private render(): void {
    if (!this.root) return;
    this.renderBoard();
    this.renderScreen();
    this.renderModal();
    this.updateLiveStatus();
  }

  private panelStructureKey(): string {
    return this.sim ? this.tab : '';
  }

  private renderBoard(): void {
    const panel = this.root?.querySelector<HTMLElement>('#operator-panel');
    if (!panel) return;
    if (this.screen !== 'board' || !this.sim) {
      if (panel.innerHTML) panel.innerHTML = '';
      delete panel.dataset.structureKey;
      return;
    }
    const structureKey = this.panelStructureKey();
    if (panel.dataset.structureKey !== structureKey) {
      panel.innerHTML = this.boardMarkup();
      panel.dataset.structureKey = structureKey;
    }
    this.patchBoardHud();
  }

  private boardMarkup(): string {
    const entries = CATALOGUE.filter((entry) => entry.tab === this.tab);
    return `
      <div class="cd1-tabs" role="tablist">
        ${(['basic', 'advanced', 'buildings'] as const).map((tab) => `<button role="tab" aria-selected="false" class="cd1-tab" data-action="tab" data-tab="${tab}">${tab}</button>`).join('')}
      </div>
      <div class="cd1-build-list" aria-label="Build catalogue">
        ${entries.map((entry) => `<button class="cd1-build-row" data-action="build" data-catalogue="${entry.id}" data-structure="${entry.id}" data-locked="false" aria-describedby="catalogue-description-${entry.id}">
          <span class="cd1-build-icon" aria-hidden="true"><img src="${structureIcon(entry.id)}" alt=""></span>
          <span class="cd1-build-copy"><b>${escapeHtml(entry.label)}</b><small></small><em></em></span>
          <span class="cd1-build-description" id="catalogue-description-${entry.id}" role="tooltip">${escapeHtml(entry.description)}</span>
        </button>`).join('')}
      </div>
      <div class="cd1-abilities-title">ABILITIES</div>
      <div class="cd1-abilities" aria-label="Abilities">
        <button class="cd1-ability sell" data-action="sell"><i aria-hidden="true">$</i><span>Sell</span></button>
        ${(['missile', 'wall', 'stop-time'] as const).map((ability) => `<button class="cd1-ability locked" data-action="ability" data-ability="${ability}" data-locked="true"><i aria-hidden="true">${ability === 'missile' ? '&#9650;' : ability === 'wall' ? '&#9632;' : '&#9203;'}</i><span>LOCKED</span></button>`).join('')}
      </div>
      <section class="cd1-next-wave" aria-live="polite">
        <div><b data-testid="wave-number"></b><time data-testid="wave-countdown"></time></div>
        <strong data-testid="wave-preview"></strong>
        <small data-testid="board-notice"></small>
      </section>
      <div class="cd1-bottom-bar">
        <button class="cd1-play" data-action="play"><i aria-hidden="true">&#9654;</i><span data-testid="play-label">PLAY</span></button>
        <div class="cd1-readouts"><b data-testid="lives-readout"></b><strong data-testid="cash-readout"></strong><small data-testid="score-readout"></small></div>
        <div class="cd1-system-controls"><button data-action="pause" aria-label="Pause simulation">&#10074;&#10074;</button><button data-action="open-quit" aria-label="Quit run">&#10005;</button><button data-action="toggle-music" data-testid="music-toggle"></button><button data-action="toggle-sfx" data-testid="sfx-toggle"></button></div>
      </div>`;
  }

  private patchBoardHud(): void {
    const panel = this.root?.querySelector<HTMLElement>('#operator-panel');
    if (!panel || this.screen !== 'board' || !this.sim) return;
    const state = this.sim.getState();
    const snapshot = this.sim.getSnapshot();
    const progressById = new Map(snapshot.progress.map((progress) => [progress.id, progress]));
    const nextWave = snapshot.nextWave;
    const programComplete = nextWave === null;
    const selectedStructure = this.mode.kind === 'build' ? this.mode.structureId : null;

    panel.querySelectorAll<HTMLButtonElement>('[data-tab]').forEach((button) => {
      const active = button.dataset.tab === this.tab;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', String(active));
    });
    panel.querySelectorAll<HTMLButtonElement>('[data-catalogue]').forEach((button) => {
      const id = button.dataset.catalogue as CatalogueId;
      const entry = CATALOGUE.find((candidate) => candidate.id === id);
      if (!entry) return;
      const unlocked = this.sim?.isUnlocked(id) ?? false;
      const progress = progressById.get(id);
      button.classList.toggle('is-locked', !unlocked);
      button.classList.toggle('is-selected', selectedStructure === id);
      button.dataset.locked = String(!unlocked);
      button.setAttribute('aria-label', unlocked ? `${entry.label}. Ready.` : `${entry.label}. Locked — ${entry.requirement}`);
      const copy = button.querySelector<HTMLElement>('small');
      const stateLabel = button.querySelector<HTMLElement>('em');
      const requirement = progress?.active ? `${progress.current}/${progress.target} ${entry.requirement}` : 'COMPLETE PRIOR RESEARCH';
      if (copy) copy.textContent = unlocked ? `${entry.domains} · $${entry.cost}` : entry.requirement;
      if (stateLabel) stateLabel.textContent = unlocked ? 'READY' : progress?.active ? `${progress.current}/${progress.target}` : 'LOCKED';
      if (!unlocked) {
        button.setAttribute('aria-label', `${entry.label}. Locked: ${requirement}`);
        if (copy) copy.textContent = requirement;
      }
    });
    const sell = panel.querySelector<HTMLButtonElement>('[data-action="sell"]');
    sell?.classList.toggle('is-active', this.mode.kind === 'sell');
    panel.querySelectorAll<HTMLButtonElement>('[data-ability]').forEach((button) => {
      const ability = button.dataset.ability as AbilityId;
      const sources = this.sim?.getAbilitySourceCount(ability) ?? 0;
      const cooldown = state.abilityCooldowns[ability] ?? 0;
      const ready = sources > 0 && cooldown === 0;
      button.classList.toggle('locked', !ready);
      button.classList.toggle('is-active', this.mode.kind === 'ability' && this.mode.ability === ability);
      button.dataset.locked = String(!ready);
      button.setAttribute('aria-label', ready ? `${abilityLabel(ability)}, ready` : `${abilityLabel(ability)}. Locked — ${sources === 0 ? abilityRequirement(ability) : `${abilityLabel(ability)} recharging`}`);
      const label = button.querySelector<HTMLElement>('span');
      if (label) label.textContent = sources === 0 ? 'LOCKED' : cooldown > 0 ? `${Math.ceil(cooldown / sources / 24)}s` : abilityLabel(ability);
    });
    const waveNumber = panel.querySelector<HTMLElement>('[data-testid="wave-number"]');
    const countdown = panel.querySelector<HTMLElement>('[data-testid="wave-countdown"]');
    const preview = panel.querySelector<HTMLElement>('[data-testid="wave-preview"]');
    const notice = panel.querySelector<HTMLElement>('[data-testid="board-notice"]');
    if (waveNumber) {
      waveNumber.textContent = programComplete
        ? 'Attack program complete'
        : `Next attack: #${nextWave.absoluteWave}${state.runMode === 'infinite' ? ` · C${nextWave.cycle} · HP ${nextWave.hpPercent}%` : ''}`;
    }
    if (countdown) countdown.textContent = programComplete ? '--:--' : formatCountdown(snapshot.nextWaveCountdownTicks);
    if (preview) preview.textContent = formatWavePreview(this.selectedMap, this.selectedDifficulty, state.nextWaveIndex, state.runMode);
    const infiniteStatus = state.runMode === 'infinite'
      ? snapshot.activeWave
        ? `ATTACK #${snapshot.activeWave.absoluteWave} ACTIVE · C${snapshot.activeWave.cycle} · BASE ${snapshot.activeWave.baseWave} · HP ${snapshot.activeWave.hpPercent}%`
        : state.phase === 'prewave' && nextWave?.baseWave === 1 && nextWave.completedCycles > 0
          ? `CYCLE ${nextWave.cycle} READY · ENEMY HP ${nextWave.hpPercent}%`
          : ''
      : '';
    const defaultStatus = programComplete ? (state.phase === 'victory' ? 'Canyon secured.' : 'WAIT: final hostiles remain.')
      : infiniteStatus || (state.phase === 'active' ? 'ATTACK ACTIVE'
        : selectedStructure ? `${structureLabel(selectedStructure)} selected`
          : this.mode.kind === 'ability' ? 'Select a target point'
            : this.mode.kind === 'sell' ? 'Select a structure to sell'
              : 'Ready for attack');
    if (notice) notice.textContent = this.uiNotice || defaultStatus;
    const play = panel.querySelector<HTMLButtonElement>('[data-action="play"]');
    const playLabel = panel.querySelector<HTMLElement>('[data-testid="play-label"]');
    const playDisabled = state.paused || state.phase === 'victory' || state.phase === 'defeat' || programComplete;
    if (play) play.disabled = playDisabled;
    if (playLabel) playLabel.textContent = state.phase === 'victory' ? 'COMPLETE' : programComplete ? 'WAIT' : 'PLAY';
    const lives = panel.querySelector<HTMLElement>('[data-testid="lives-readout"]');
    const cash = panel.querySelector<HTMLElement>('[data-testid="cash-readout"]');
    const score = panel.querySelector<HTMLElement>('[data-testid="score-readout"]');
    if (lives) lives.textContent = `★ ${snapshot.lives}`;
    if (cash) cash.textContent = `$${snapshot.cash}`;
    if (score) score.textContent = `SCORE ${snapshot.score}`;
    const audioSettings = this.audio.getSettings();
    const musicToggle = panel.querySelector<HTMLButtonElement>('[data-action="toggle-music"]');
    const sfxToggle = panel.querySelector<HTMLButtonElement>('[data-action="toggle-sfx"]');
    if (musicToggle) {
      musicToggle.textContent = audioSettings.musicMuted ? 'M×' : 'M♪';
      musicToggle.classList.toggle('is-muted', audioSettings.musicMuted);
      musicToggle.setAttribute('aria-pressed', String(!audioSettings.musicMuted));
      musicToggle.setAttribute('aria-label', audioSettings.musicMuted ? 'Music muted. Activate music.' : 'Music on. Mute music.');
    }
    if (sfxToggle) {
      sfxToggle.textContent = audioSettings.sfxMuted ? 'S×' : 'S♪';
      sfxToggle.classList.toggle('is-muted', audioSettings.sfxMuted);
      sfxToggle.setAttribute('aria-pressed', String(!audioSettings.sfxMuted));
      sfxToggle.setAttribute('aria-label', audioSettings.sfxMuted ? 'Sound effects muted. Activate sound effects.' : 'Sound effects on. Mute sound effects.');
    }
    this.updateLiveStatus();
  }

  private renderScreen(): void {
    const overlay = this.root?.querySelector<HTMLElement>('#screen-overlay');
    if (!overlay) return;
    if (this.screen === 'board') { overlay.innerHTML = ''; return; }
    if (this.screen === 'title') {
      overlay.innerHTML = `<section class="cd1-menu cd1-title-screen"><div class="cd1-title-battle-art" aria-hidden="true"><i class="cd1-title-rock rock-a"></i><i class="cd1-title-rock rock-b"></i><i class="cd1-title-rock rock-c"></i><i class="cd1-title-unit ground"></i><i class="cd1-title-unit air"></i><i class="cd1-title-burst"></i></div><div class="cd1-title-plate"><p>ARCADE GAME</p><h1><span>CANYON</span><span>DEFENSE</span></h1><div class="cd1-title-stripe"></div></div><div class="cd1-title-actions"><button class="cd1-metal-button primary" data-action="open-maps" autofocus>PLAY</button><button class="cd1-metal-button" data-action="open-help">HELP</button><button class="cd1-metal-button" data-action="open-scores">SCORES</button></div><small>Mouse: place tower · Space: start attack · P: pause</small></section>`;
      return;
    }
    if (this.screen === 'maps') {
      overlay.innerHTML = `<section class="cd1-menu cd1-map-screen"><h2>SELECT MAP</h2><div class="cd1-map-options">${(Object.values(MAPS)).map((map, index) => `<button class="cd1-map-option cd1-map-${index + 1}" data-action="select-map" data-map="${map.id}"><span class="cd1-map-thumb" aria-hidden="true">${mapThumbnail(map)}</span><b>MAP ${index + 1}</b><small>${escapeHtml(map.descriptor)}</small></button>`).join('')}</div><button class="cd1-back-button" data-action="back-title">BACK</button></section>`;
      return;
    }
    if (this.screen === 'difficulty') {
      overlay.innerHTML = `<section class="cd1-menu cd1-difficulty-screen"><h2>SELECT DIFFICULTY</h2><p>MAP ${this.selectedMap.slice(-1)}</p><div class="cd1-run-mode-selector" role="group" aria-label="Run mode"><button data-action="select-run-mode" data-run-mode="fixed" data-testid="run-mode-fixed" aria-pressed="${this.selectedRunMode === 'fixed'}" class="${this.selectedRunMode === 'fixed' ? 'is-selected' : ''}"><b>FIXED</b><small>Authored attack program</small></button><button data-action="select-run-mode" data-run-mode="infinite" data-testid="run-mode-infinite" aria-pressed="${this.selectedRunMode === 'infinite'}" class="${this.selectedRunMode === 'infinite' ? 'is-selected' : ''}"><b>INFINITE</b><small>Repeating cycle · rising HP</small></button></div><div class="cd1-difficulty-options">${(Object.entries(DIFFICULTIES) as [DifficultyId, typeof DIFFICULTIES.easy][]).map(([id, difficulty]) => `<button class="cd1-difficulty ${id}" data-action="select-difficulty" data-difficulty="${id}"><b>${difficulty.label.toUpperCase()}</b><span>${this.selectedRunMode === 'fixed' ? `${difficulty.waves} attacks` : `${WAVE_SCRIPTS[this.selectedMap][id].length} attack cycle`}</span><span>attack delay ${difficulty.interval}</span><em>${this.selectedRunMode === 'fixed' ? `score bonus ${difficulty.bonus}` : `HP +${Math.round((infiniteHealthMultiplier(id, 1) - 1) * 100)}% / cycle`}</em></button>`).join('')}</div><button class="cd1-back-button" data-action="back-maps">BACK</button></section>`;
      return;
    }
    if (this.screen === 'help') {
      overlay.innerHTML = `<section class="cd1-menu cd1-info-screen"><h2>HELP</h2><p>Choose a map, run mode, and difficulty, then select a ready construction and click solid canyon terrain. Towers use 1×1 cells; buildings use 2×2.</p><p>Fixed ends after its authored attack program. Infinite repeats that program forever; enemy HP rises additively each cycle, and the run ends only on defeat or quit.</p><p>Attacks start on their countdown. PLAY or Space releases the next queued attack early. SELL recovers 50%; P pauses.</p><p>Build and combat progression unlock the remaining catalogue and abilities. Locked controls can be clicked to show their exact requirement.</p><button class="cd1-back-button" data-action="back-title">BACK</button></section>`;
      return;
    }
    const scores = this.loadScores();
    overlay.innerHTML = `<section class="cd1-menu cd1-info-screen"><h2>SCORES</h2>${scores.length ? scoreListMarkup(scores) : '<p>No scores yet.</p>'}<button class="cd1-back-button" data-action="back-title">BACK</button></section>`;
  }

  private updateResultModal(): void {
    if (!this.sim || this.modal) return;
    const state = this.sim.getState();
    if (state.phase === 'victory' || state.phase === 'defeat') {
      this.recordScore(state.phase);
      this.modal = 'result';
      this.audio.setPaused(true);
      this.renderModal();
    }
  }

  private renderModal(): void {
    const overlay = this.root?.querySelector<HTMLElement>('#modal-overlay');
    if (!overlay) return;
    if (!this.modal) { overlay.classList.remove('is-visible'); overlay.innerHTML = ''; return; }
    overlay.classList.add('is-visible');
    if (this.modal === 'pause') {
      overlay.innerHTML = `<section class="cd1-modal-card" role="dialog" aria-modal="true" aria-label="Paused"><h2>Paused</h2><p>CLICK TO CONTINUE</p><button class="cd1-modal-hitbox" data-action="resume" aria-label="Resume"></button><button class="cd1-modal-quit" data-action="open-quit">QUIT</button></section>`;
      return;
    }
    if (this.modal === 'quit') {
      overlay.innerHTML = `<section class="cd1-modal-card quit" role="dialog" aria-modal="true" aria-label="Confirm quit"><h2>Quit?</h2><p>Are you sure?</p><div><button class="cd1-choice" data-action="quit-title">Yes</button><button class="cd1-choice" data-action="cancel-quit">No</button></div></section>`;
      return;
    }
    const snapshot = this.sim?.getSnapshot();
    const won = snapshot?.phase === 'victory';
    const resultCopy = snapshot?.runMode === 'infinite'
      ? `Wave ${snapshot.nextWaveIndex} reached · ${completedCyclesForClearedWaves(snapshot.completedWaves, WAVE_SCRIPTS[snapshot.mapId][snapshot.difficultyId].length)} cycles · ${formatSurvivalTime(snapshot.tick)} survived.`
      : won ? 'All attacks survived.' : 'All lives depleted.';
    overlay.innerHTML = `<section class="cd1-modal-card result" role="dialog" aria-modal="true" aria-label="Run result"><h2>${won ? 'Victory' : 'Defeat'}</h2><p>${resultCopy}</p><div><button class="cd1-choice" data-action="restart">Restart</button><button class="cd1-choice" data-action="result-title">Title</button></div></section>`;
  }

  private loadScores(): readonly StoredScore[] {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(SCORE_STORAGE_KEY) ?? '[]') as unknown;
      if (!Array.isArray(parsed)) return [];
      return rankScoresByCategory(parsed.map(normalizeStoredScore).filter((entry): entry is StoredScore => entry !== null));
    } catch { return []; }
  }

  private recordScore(outcome: 'victory' | 'defeat' | 'quit'): void {
    if (!this.sim) return;
    const snapshot = this.sim.getSnapshot();
    if (snapshot.runMode === 'fixed' && outcome === 'quit') return;
    const key = `${snapshot.runMode}:${outcome}:${snapshot.tick}`;
    if (this.recordedResultKey === key) return;
    this.recordedResultKey = key;
    const entry = storedScoreForSnapshot(snapshot, outcome);
    try { window.localStorage.setItem(SCORE_STORAGE_KEY, JSON.stringify(rankScoresByCategory([entry, ...this.loadScores()]))); } catch { /* Storage is optional presentation history. */ }
  }

  private updateLiveStatus(): void {
    const status = this.root?.querySelector<HTMLElement>('#screen-reader-status');
    if (status && this.sim) status.textContent = this.uiNotice || this.sim.getSnapshot().lastNotice;
  }
}
